import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMediaQuery, useTheme } from '@mui/material';
import { useFormContext, } from "react-hook-form-mui";
import { useI18N } from '../../context/I18NContext';
import { priceLabel } from '../../helpers/general';
import useWidgetStore from '../../store/useWidgetStore';
import { CustomSelectElement } from '../../layouts/Theme';
import useDataStore from '../../store/useDataStore';
// the placeholder each source prompts with. `props.source` is a code name, not
// something a consumer should read. Routed through t(), so these can be translated
// like any other string.
const SOURCE_PLACEHOLDERS = {
    group: 'Selecteer een categorie',
    apptype: 'Selecteer een afspraaktype',
    resource: 'Selecteer een resource'
};
export default function ObjectSelectPicker(props) {
    var _a, _b, _c;
    const theme = useTheme();
    const { t, ts } = useI18N();
    const isLarge = useMediaQuery(theme.breakpoints.up('sm'));
    const { selectedApptype, selectedProduct, selectedCategory, selectedGroup, selectedResource, setSelectedApptype, setSelectedGroup, setSelectedResource, getSourceHelpers } = useWidgetStore();
    const { resources, apptypes } = useDataStore();
    const { register, setValue } = useFormContext();
    // re-evaluated on every render: this component subscribes to all of the
    // selected* state the sources above it write to
    const dependenciesMet = ((_a = props.dependsOn) !== null && _a !== void 0 ? _a : []).every((source) => { var _a, _b; return ((_b = (_a = getSourceHelpers(source).type) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0; });
    const [options, setOptions] = useState();
    // What the select actually offers. The store holds every object of this source, but the
    // view can hand over a narrower list - the apptypes of the chosen category, say - and a
    // selection has to be valid against the list on screen, not against the whole store.
    const availableOptions = () => {
        var _a;
        let sourceHelper = getSourceHelpers((_a = props.source) !== null && _a !== void 0 ? _a : 'apptype');
        return props.data && props.data.length > 0 ? props.data : sourceHelper.options;
    };
    // A select with one option is not a choice: the consumer would have to open it only to
    // confirm the single thing on offer. So for 'group' and 'apptype' it is not drawn at all
    // - the option travels with the form as a hidden field instead (see the effect below,
    // which selects it). 'group' stays fully silent about it (Select.tsx draws no label for
    // it either); 'apptype' gets a plain-text label drawn above it by Select.tsx
    // (showSingleOptionLabel). Every other source - resource, product, category, and any
    // source defined later - always shows the real select, even with one option on it: those
    // are not part of the group -> apptype -> resource chain collapse this was built for, and
    // a resource/product/category picker with one option is still worth seeing as a select.
    // The "Geen voorkeur" option is only added when there is more than one object, so a
    // one-object list really does mean one option. Undefined = draw the select.
    // Not while dependencies are unmet: the list is still the unfiltered one then, and the
    // option that would be forced on the consumer is not necessarily the one that survives
    // the select above making its choice.
    const onlyOption = () => {
        if (props.source !== 'group' && props.source !== 'apptype') {
            return undefined;
        }
        let offered = availableOptions();
        return dependenciesMet && offered.length === 1 ? offered[0] : undefined;
    };
    const renderOption = (row) => {
        var _a;
        if (props.renderOption) {
            return props.renderOption(row);
        }
        let price = props.source === 'apptype' && ((_a = props.config) === null || _a === void 0 ? void 0 : _a.showPrice) ? priceLabel(row, { ts }) : '';
        return price ? `${row.name} ( ${price} )` : row.name;
    };
    const renderPlaceholder = (text) => props.renderPlaceholder
        ? props.renderPlaceholder(text)
        : (_jsx(Typography, { component: "span", sx: { color: theme.palette.text.secondary }, children: text }));
    // the closed select renders the selected option through the same renderer as the dropdown,
    // so a value can never show up as an empty box
    const renderSelectedValue = (value) => {
        var _a, _b, _c, _d;
        let id = parseInt(`${value}`);
        // the placeholder is the prompt for the source, not the configured label
        let placeholder = t((_b = SOURCE_PLACEHOLDERS[(_a = props.source) !== null && _a !== void 0 ? _a : '']) !== null && _b !== void 0 ? _b : `Selecteer een ${props.source}`);
        // 0 is the store's "nothing selected", -1 is the real "Geen voorkeur" option
        if (!dependenciesMet || isNaN(id) || id === 0 || `${value}`.length === 0) {
            return renderPlaceholder(placeholder);
        }
        let row = (_d = (_c = props.data) === null || _c === void 0 ? void 0 : _c.find) === null || _d === void 0 ? void 0 : _d.call(_c, (item) => item.id === id);
        if (!row) {
            return id === -1 ? renderOption({ id, name: t('Geen voorkeur', props.source) }) : null;
        }
        return renderOption(row);
    };
    useEffect(() => {
        var _a;
        let _options = [];
        if (((_a = props.config) === null || _a === void 0 ? void 0 : _a.showAnyOption) && props.data.length > 1) {
            _options.push({ id: -1, label: renderOption({ id: -1, name: t('Geen voorkeur', props.source) }) });
        }
        props.data.forEach((item) => {
            _options.push({ id: item.id, label: renderOption(item) });
        });
        setOptions(_options);
    }, [props]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (props.active === false) {
            return;
        }
        let sourceHelper = getSourceHelpers((_a = props.source) !== null && _a !== void 0 ? _a : 'apptype');
        let offered = availableOptions();
        if (dependenciesMet && sourceHelper.type && offered.length > 0) {
            let validObjects = offered.map((object) => object.id);
            if (((_b = props.config) === null || _b === void 0 ? void 0 : _b.showAnyOption) && offered.length > 1) {
                validObjects.push(-1);
            }
            if (offered.length === 1) {
                // the select is a hidden field: the only option is the selection, no matter what
                // defaultValue says and no matter what was picked while more was on offer
                setValue(props.source, offered[0].id);
                if (sourceHelper.type[0] !== offered[0].id) {
                    // guarded: the setter writes a new array every time, and this effect
                    // watches the state it writes to
                    sourceHelper.setter(offered[0].id);
                }
                return;
            }
            if (sourceHelper.type.length > 0) {
                // we already have a selection, make sure its still valid
                if (validObjects.includes(sourceHelper.type[0])) {
                    setValue(props.source, sourceHelper.type[0]);
                }
                else {
                    if (validObjects.length > 0) {
                        // not valid anymore - the category changed under it, say - so move to the
                        // first option that is on offer, in the store as well as in the form
                        setValue(props.source, validObjects[0]);
                        sourceHelper.setter(validObjects[0]);
                    }
                }
            }
            else if (sourceHelper.type.length === 0) {
                // there is no selection, but we have options loaded, handle first and false
                if ((_c = props.config) === null || _c === void 0 ? void 0 : _c.defaultValue) {
                    if (((_d = props.config) === null || _d === void 0 ? void 0 : _d.defaultValue) === 'first') {
                        setValue(props.source, offered[0].id);
                        sourceHelper.setter(offered[0].id);
                    }
                    else if (typeof ((_e = props.config) === null || _e === void 0 ? void 0 : _e.defaultValue) == 'number') {
                        if (validObjects.includes((_f = props.config) === null || _f === void 0 ? void 0 : _f.defaultValue)) {
                            // this also handles -1
                            setValue(props.source, (_g = props.config) === null || _g === void 0 ? void 0 : _g.defaultValue);
                        }
                        else {
                            // select the first option if the default option isnt available
                            setValue(props.source, validObjects[0]);
                        }
                    }
                }
                else if (((_h = props.config) === null || _h === void 0 ? void 0 : _h.defaultValue) === false) {
                    // explicitly select nothing
                    let currentOptions = validObjects.filter((option) => option !== -1);
                    if (currentOptions.length === 1) {
                        // if we have only 1 option left, select it
                        setValue(props.source, currentOptions[0]);
                    }
                }
            }
        }
    }, [selectedResource, selectedApptype, selectedCategory, selectedGroup, selectedProduct, resources, apptypes, props]);
    const onChange = (value) => {
        var _a;
        setValue(props.source, value);
        let sourceHelper = getSourceHelpers((_a = props.source) !== null && _a !== void 0 ? _a : 'apptype');
        sourceHelper.setter(parseInt(value));
    };
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let sourceHelper = getSourceHelpers((_a = props.source) !== null && _a !== void 0 ? _a : 'apptype');
        if ((_b = props.config) === null || _b === void 0 ? void 0 : _b.resetSelection) {
            setSelectedApptype(0);
            setSelectedResource(0);
            setSelectedGroup(0);
        }
        else if (dependenciesMet && ((_c = sourceHelper.type) === null || _c === void 0 ? void 0 : _c.length) === 0) {
            if ((_d = props.config) === null || _d === void 0 ? void 0 : _d.defaultValue) {
                sourceHelper.setter(((_e = props.config) === null || _e === void 0 ? void 0 : _e.defaultValue) === 'first' ? ((_g = (_f = availableOptions()[0]) === null || _f === void 0 ? void 0 : _f.id) !== null && _g !== void 0 ? _g : 0) : (_h = props.config) === null || _h === void 0 ? void 0 : _h.defaultValue);
            }
        }
    }, []);
    const single = onlyOption();
    if (single) {
        // valueAsNumber: the DOM hands back a string, and the view mirrors this field into the
        // store expecting the id as a number
        return (_jsx("input", Object.assign({}, register(props.source, { valueAsNumber: true }), { type: "hidden", value: single.id, readOnly: true })));
    }
    return (_jsx(Box, { sx: { minWidth: 120, my: 1, maxWidth: isLarge ? '500px' : '100%' }, children: _jsx(CustomSelectElement, { id: props.source, name: props.source, label: props.hideLabel ? undefined : t((_c = (_b = props.config) === null || _b === void 0 ? void 0 : _b.label) !== null && _c !== void 0 ? _c : ''), disabled: !dependenciesMet, onChange: onChange, options: options, 
            // the placeholder sits in the value slot, so an empty field is not empty
            // to look at: leave the label shrunk or it lands on top of the prompt
            InputLabelProps: props.hideLabel ? undefined : { shrink: true }, sx: props.sx, SelectProps: Object.assign({ 
                // without this MUI skips renderValue while the field is empty,
                // and the placeholder never gets a chance to render
                displayEmpty: true, renderValue: renderSelectedValue }, (props.menuPaperSx
                ? {
                    MenuProps: {
                        // the menu portals out of the widget root, so the option
                        // colours have to travel with it, through sx
                        slotProps: {
                            paper: {
                                sx: props.menuPaperSx
                            }
                        }
                    }
                }
                : {})) }) }));
}
